import { useCallback, useEffect, useState } from 'react';
import { errorMessage, isApiError } from '@/core/http';
import { Button, Dialog, Page } from '@/core/ui';
import { useFlowDraft } from '../application/draft';
import { useDeleteFlow, usePublishFlow, useUnpublishFlow, useValidateFlow } from '../application/queries';
import { issuesFromContext } from '../domain/issues';
import type { Flow, FlowDoc, FlowIssue } from '../domain/types';
import { BuilderHeader, type BuilderTab } from './builder/BuilderHeader';
import { FlowEditor, type TestRequest } from './builder/FlowEditor';
import type { PanelTab } from './builder/SidePanel';
import { FlowResults } from './FlowResults';

interface Props {
  flow: Flow;
  tab: BuilderTab;
  onTab: (tab: BuilderTab) => void;
  onBack: () => void;
  onDeleted: () => void;
}

/**
 * O builder de um fluxo: o topo (nome, situação, publicar) e as abas
 * "Fluxo" (o canvas) e "Resultados". O rascunho vive aqui — trocar de aba
 * não perde nada — e se salva sozinho.
 */
export function FlowBuilder({ flow, tab, onTab, onBack, onDeleted }: Props) {
  const draft = useFlowDraft(flow);
  const { server, setServer, flush } = draft;
  const publish = usePublishFlow();
  const unpublish = useUnpublishFlow();
  const remove = useDeleteFlow();
  const validate = useValidateFlow();
  const [panel, setPanel] = useState<PanelTab>('editar');
  const [test, setTest] = useState<TestRequest | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [checked, setChecked] = useState<{ doc: FlowDoc; issues: FlowIssue[] } | null>(null);

  // "Verificar agora" vale enquanto o rascunho for o mesmo; depois, o que a gravação devolveu.
  const issues = checked && checked.doc === draft.draft.doc ? checked.issues : server.issues;

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 4000);
    return () => window.clearTimeout(t);
  }, [toast]);

  // Ctrl/Cmd+S grava já, de qualquer lugar do builder.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        void flush();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [flush]);

  const showIssues = useCallback(() => {
    onTab('fluxo');
    setTest(null);
    setPanel('problemas');
  }, [onTab]);

  const doPublish = async () => {
    // Publica o rascunho salvo: o que está na tela precisa chegar antes.
    if (!(await flush())) {
      setToast('Não deu para salvar o rascunho. Tente publicar de novo.');
      return;
    }
    publish.mutate(flow.id, {
      onSuccess: (out) => {
        setServer(out);
        setToast(`Versão ${out.live_version} publicada — já vale no portal.`);
      },
      onError: (e) => {
        if (isApiError(e) && e.is('flow_not_publishable')) {
          const found = issuesFromContext(e.context);
          if (found.length) setServer((s) => ({ ...s, issues: found }));
          setToast('Corrija os erros para publicar.');
          showIssues();
        } else setToast(errorMessage(e));
      },
    });
  };

  const doUnpublish = () =>
    unpublish.mutate(flow.id, {
      onSuccess: (out) => {
        setServer(out);
        setToast('Fluxo fora do ar: o portal não o mostra mais.');
      },
      onError: (e) => setToast(errorMessage(e)),
    });

  const check = () =>
    validate.mutate(draft.draft.doc, {
      onSuccess: (out) => setChecked({ doc: draft.draft.doc, issues: out.issues }),
      onError: (e) => setToast(errorMessage(e)),
    });

  return (
    <div className="flex h-full min-h-[36rem] flex-col">
      <BuilderHeader
        name={draft.draft.name}
        description={draft.draft.description}
        onMeta={draft.setMeta}
        server={server}
        saveState={draft.saveState}
        onRetrySave={() => void flush()}
        tab={tab}
        onTab={onTab}
        onBack={onBack}
        onTest={() => {
          onTab('fluxo');
          setTest({ run: Date.now() });
        }}
        onPublish={() => void doPublish()}
        publishing={publish.isPending}
        onUnpublish={doUnpublish}
        onDelete={() => setConfirmDelete(true)}
      />

      {tab === 'fluxo' ? (
        <FlowEditor
          doc={draft.draft.doc}
          flowName={draft.draft.name}
          update={draft.updateDoc}
          issues={issues}
          onCheck={check}
          checking={validate.isPending}
          panel={panel}
          onPanel={setPanel}
          test={test}
          onTest={setTest}
        />
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <Page>
            <FlowResults flowId={flow.id} />
          </Page>
        </div>
      )}

      {toast && (
        <div
          role="status"
          className="fixed bottom-6 left-1/2 z-50 max-w-[90vw] -translate-x-1/2 rounded-2xl bg-gray-900 px-4 py-2.5 text-sm text-white shadow-lg"
        >
          {toast}
        </div>
      )}

      <Dialog
        open={confirmDelete}
        onClose={() => {
          setConfirmDelete(false);
          remove.reset();
        }}
        title="Excluir este fluxo?"
        description={draft.draft.name}
      >
        {isApiError(remove.error) && remove.error.is('flow_has_sessions') ? (
          <p className="rounded-xl bg-surface-2 p-3 text-sm text-fg">
            Este fluxo já tem conversas registradas — excluir apagaria os resultados. Para tirá-lo do portal,
            use “Despublicar”.
          </p>
        ) : (
          <p className="text-sm text-fg-muted">
            O rascunho e as versões somem, e ele sai da entrada da Centuria (boas-vindas e atalhos). Não dá
            para desfazer.
          </p>
        )}
        {remove.isError && !(isApiError(remove.error) && remove.error.is('flow_has_sessions')) && (
          <p role="alert" className="mt-3 text-sm text-fg">
            {errorMessage(remove.error)}
          </p>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setConfirmDelete(false)}>
            Cancelar
          </Button>
          {isApiError(remove.error) && remove.error.is('flow_has_sessions') ? (
            <Button
              disabled={server.live_version === null}
              loading={unpublish.isPending}
              onClick={() => {
                setConfirmDelete(false);
                remove.reset();
                doUnpublish();
              }}
            >
              Despublicar
            </Button>
          ) : (
            <Button
              loading={remove.isPending}
              onClick={() => remove.mutate(flow.id, { onSuccess: onDeleted })}
            >
              Excluir
            </Button>
          )}
        </div>
      </Dialog>
    </div>
  );
}
